// Display list and draw dispatch (Fase 1 strangler).
// All framebuffer writes go through executeScene() when the guard is enabled.

import type { Surface, Rect } from './surface';
import {
  screen,
  drawRectOn,
  fillSurface,
  draw_charOn,
  blitGame,
  DrawCircle,
  DrawCircleEx,
  ThinLine,
  Arc,
} from './surface';
import { SetGreyscalePalette, SetTitlePalette2, SetTonedPalette } from './palette';
import { RES_SCALE } from '../core/constants';
import {
  assertDrawContext,
  enterExecuteScene,
  leaveExecuteScene,
} from './scene-guard';

export type PaletteOp =
  | { op: 'greyscale' }
  | { op: 'title'; tick: number }
  | { op: 'toned'; dist: number };

export type SceneCmd =
  | { t: 'fill'; color: number }
  | { t: 'rect'; x: number; y: number; w: number; h: number; c: number }
  | { t: 'circle'; x: number; y: number; r: number; c: number }
  | { t: 'circleEx'; x: number; y: number; r: number; r2: number; c: number }
  | { t: 'thinLine'; x1: number; y1: number; x2: number; y2: number; c: number }
  | { t: 'arc'; x: number; y: number; r: number; dir: number }
  | { t: 'text'; x: number; y: number; s: string; c: number }
  | { t: 'blitGame'; src: Surface; rect: Rect | null; dx: number; dy: number; nativeScale: number }
  | { t: 'palette'; p: PaletteOp };

let recording: SceneCmd[] | null = null;

const tr = Math.trunc;

export { enableSceneGuard, isInsideExecuteScene } from './scene-guard';

/** Run draw callbacks; the only path that may touch the framebuffer when guarded. */
export function executeScene(fn: () => void): void {
  enterExecuteScene();
  try {
    fn();
  } finally {
    leaveExecuteScene();
  }
}

/** Record draw commands for inspection/tests without applying. */
export function recordScene(fn: () => void): SceneCmd[] {
  const cmds: SceneCmd[] = [];
  const prev = recording;
  recording = cmds;
  enterExecuteScene();
  try {
    fn();
  } finally {
    leaveExecuteScene();
    recording = prev;
  }
  return cmds;
}

export function pushSceneCmd(cmd: SceneCmd): void {
  recording?.push(cmd);
}

export function sceneFill(color: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'fill', color });
    return;
  }
  fillSurface(screen, color);
}

export function sceneRect(x: number, y: number, w: number, h: number, c: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'rect', x, y, w, h, c });
    return;
  }
  drawRectOn(screen, x, y, w, h, c);
}

export function sceneCircle(x: number, y: number, r: number, c: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'circle', x, y, r, c });
    return;
  }
  DrawCircle(x, y, r, c);
}

export function sceneCircleEx(x: number, y: number, r: number, r2: number, c: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'circleEx', x, y, r, r2, c });
    return;
  }
  DrawCircleEx(x, y, r, r2, c);
}

export function sceneThinLine(x1: number, y1: number, x2: number, y2: number, c: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'thinLine', x1, y1, x2, y2, c });
    return;
  }
  ThinLine(x1, y1, x2, y2, c);
}

export function sceneArc(x: number, y: number, r: number, dir: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'arc', x, y, r, dir });
    return;
  }
  Arc(x, y, r, dir);
}

export function sceneText(x: number, y: number, s: string, c: number): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'text', x, y, s, c });
    return;
  }
  sceneDrawTextImpl(x, y, s, c);
}

export function sceneBlitGame(
  src: Surface,
  rect: Rect | null,
  dx: number,
  dy: number,
  nativeScale = 1
): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'blitGame', src, rect, dx, dy, nativeScale });
    return;
  }
  blitGame(src, rect, screen, dx, dy, nativeScale);
}

export function scenePalette(p: PaletteOp): void {
  assertDrawContext();
  if (recording) {
    recording.push({ t: 'palette', p });
    return;
  }
  applyPalette(p);
}

function applyPalette(p: PaletteOp): void {
  switch (p.op) {
    case 'greyscale':
      SetGreyscalePalette();
      break;
    case 'title':
      SetTitlePalette2(p.tick);
      break;
    case 'toned':
      SetTonedPalette(p.dist);
      break;
  }
}

function sceneDrawTextImpl(x: number, y: number, str: string, tcol: number): void {
  let cur_x = tr(x);
  const startX = cur_x;
  let cur_y = tr(y);
  for (let n = 0; n < str.length; n++) {
    const c = str.charCodeAt(n);
    if (c === 10) {
      cur_x = startX;
      cur_y += 10 * RES_SCALE;
    } else {
      draw_charOn(screen, cur_x, cur_y, c, tcol);
      cur_x += 8 * RES_SCALE;
    }
  }
}

/** Apply a pre-built command list (used by tests). */
export function applySceneCmds(cmds: SceneCmd[]): void {
  executeScene(() => {
    for (const cmd of cmds) {
      switch (cmd.t) {
        case 'fill':
          fillSurface(screen, cmd.color);
          break;
        case 'rect':
          drawRectOn(screen, cmd.x, cmd.y, cmd.w, cmd.h, cmd.c);
          break;
        case 'circle':
          DrawCircle(cmd.x, cmd.y, cmd.r, cmd.c);
          break;
        case 'circleEx':
          DrawCircleEx(cmd.x, cmd.y, cmd.r, cmd.r2, cmd.c);
          break;
        case 'thinLine':
          ThinLine(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.c);
          break;
        case 'arc':
          Arc(cmd.x, cmd.y, cmd.r, cmd.dir);
          break;
        case 'text':
          sceneDrawTextImpl(cmd.x, cmd.y, cmd.s, cmd.c);
          break;
        case 'blitGame':
          blitGame(cmd.src, cmd.rect, screen, cmd.dx, cmd.dy, cmd.nativeScale);
          break;
        case 'palette':
          applyPalette(cmd.p);
          break;
      }
    }
  });
}
